import { Box, Flex, Text } from '@chakra-ui/react'
import { ChartOptions } from 'chart.js'
import { useEffect, useState } from 'react'
import { Doughnut } from 'react-chartjs-2'

import { Icon } from '../../../../components/Icon'
import { LoadingBlock } from '../../../../components/Loading'
import { SectionCard, SectionTitle } from '../../../../components/SectionCard'
import { FormSelect } from '../../../../components/Select'
import { formatCompactCurrency } from '../../../../helpers'
import { IFinancialGoal } from '../../../../interfaces/IFinancialIndicators'
import FinancialIndicatorsService from '../../../../services/FinancialIndicators/FinancialIndicatorsService'
import { magenta } from '../../../../theme'

interface IPlrSectionProps {
  onError: (msg: string) => void
}

const service = new FinancialIndicatorsService()

export default function PlrSection({ onError }: IPlrSectionProps) {
  const currentYear = new Date().getFullYear()
  const [selectedYear, setSelectedYear] = useState(currentYear)
  const [goals, setGoals] = useState<IFinancialGoal[]>([])
  const [accumulatedProfit, setAccumulatedProfit] = useState(0)
  const [loading, setLoading] = useState(false)

  const years = Array.from({ length: currentYear - 2022 + 1 }, (_, i) => 2022 + i)

  useEffect(() => {
    fetchGoals()
  }, [selectedYear])

  async function fetchGoals() {
    setLoading(true)
    try {
      const res = await service.getGoals(selectedYear)
      setGoals(res.data.goals)
      setAccumulatedProfit(Number(res.data.accumulated_profit))
    } catch {
      setGoals([])
      setAccumulatedProfit(0)
      onError('Erro ao carregar metas PLR.')
    } finally {
      setLoading(false)
    }
  }

  function getPercentage(goal: IFinancialGoal): number {
    if (goal.target_value <= 0 || accumulatedProfit <= 0) return 0
    const pct = (accumulatedProfit / goal.target_value) * 100
    // Rounding would show 100% for a goal still a few reais short
    return Math.min(pct, accumulatedProfit >= goal.target_value ? 100 : 99)
  }

  function getRemainingValue(goal: IFinancialGoal): number {
    return Math.max(goal.target_value - accumulatedProfit, 0)
  }

  // Cores das metas (rosa, roxo, amarelo) como na imagem
  const goalColors = [magenta[500], '#9536B9', '#FFB31A']

  return (
    <SectionCard
      title="PLR (Participação nos Lucros e Resultados)"
      titleAs="h3"
      actions={
        <Flex align="center" gap={1}>
          <FormSelect
            size="sm"
            w="100px"
            value={selectedYear}
            onChange={(value) => setSelectedYear(Number(value))}
            options={years.map((y) => ({ value: y, label: String(y) }))}
            hideEmptyOption
          />
          <Icon name="lni lni-calendar" color="magenta.500" size="md" />
        </Flex>
      }
    >
      <SectionTitle as="h4">Atingimento</SectionTitle>

      {loading ? (
        <LoadingBlock />
      ) : goals.length === 0 ? (
        <Text variant="caption">Nenhuma meta configurada para {selectedYear}.</Text>
      ) : (
        <Flex gap={8} flexWrap="wrap">
          {goals.map((goal, index) => {
            const percentage = getPercentage(goal)
            const isAchieved = goal.achieved
            const remaining = getRemainingValue(goal)
            const color = isAchieved ? '#67B418' : goalColors[index] || goalColors[0]

            const doughnutData = {
              datasets: [
                {
                  data: [percentage, 100 - percentage],
                  backgroundColor: [color, '#f0f0f0'],
                  borderWidth: 0
                }
              ]
            }

            const doughnutOptions: ChartOptions<'doughnut'> = {
              cutout: '87%',
              animation: false,
              responsive: true,
              maintainAspectRatio: true,
              plugins: { legend: { display: false }, tooltip: { enabled: false } }
            }

            return (
              <Flex key={goal.goal_number} direction="column" align="center" minW="120px">
                <Text fontSize="xs" fontWeight="600" color="gray.600" mb={2}>
                  Meta {goal.goal_number}
                </Text>

                <Box position="relative" w="100px" h="100px">
                  <Doughnut
                    key={`goal-${goal.goal_number}-${Math.round(percentage)}`}
                    data={doughnutData}
                    options={doughnutOptions}
                  />
                  <Flex
                    position="absolute"
                    top="0"
                    left="0"
                    right="0"
                    bottom="0"
                    align="center"
                    justify="center"
                    direction="column"
                  >
                    <Text fontSize="lg" fontWeight="semibold" color="gray.800">
                      {percentage.toFixed(0)}%
                    </Text>
                  </Flex>
                </Box>

                <Flex direction="column" align="center" mt={2}>
                  {!isAchieved && remaining > 0 ? (
                    <>
                      <Text variant="caption">Faltam:</Text>
                      <Text fontSize="xs" fontWeight="600" color="gray.700">
                        {formatCompactCurrency(remaining)}
                      </Text>
                    </>
                  ) : (
                    <Text fontSize="xs" fontWeight="600" color="gray.700">
                      Meta batida
                    </Text>
                  )}
                </Flex>
              </Flex>
            )
          })}
        </Flex>
      )}
    </SectionCard>
  )
}
